import { arrayOf, bool, objectOf, oneOf, shape, string } from 'prop-types'
import { Component } from 'react'

import { IS_JEST, PAGE_TYPES, TENANT_IT } from 'shared/consts'
import { NEW_CDN } from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { isVariantAttributes } from 'shared/experiments/utils/featureFlags'
import logger from 'shared/services/logger'
import store from 'shared/store'
import { QUERY_UPDATED } from 'shared/store/ducks/events'
import MountPointsClass from 'shared/utils/mountPoints'
import { handleDummySearchBoxUpdate } from 'views/assets/scripts/mobileHeaderDummySearchBox'
import algoliaUserService from 'views/assets/scripts/services/storage/AlgoliaUserService'
import DataLayerSearchHistoryService from 'views/assets/scripts/services/storage/DataLayerSearchHistoryService'
import SearchHistoryService from 'views/assets/scripts/services/storage/SearchHistoryService'
import Storage from 'views/assets/scripts/services/storage/storage'
import { SEARCH_CHANNEL_ALGOLIA } from 'views/consts'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import buildClientAPI from 'views/providers/clientAPI'
import debounce from 'views/utils/debounce'
import {
  mapAlgoliaToSuggestResults,
  processAlgoliaQuerySuggestions,
} from 'views/utils/esCompatibilityUtils'
import { isValidProduct } from 'views/utils/productUtils'

import { ALGOLIA_QUERY_MAX_LENGTH, TYPEAHEAD_MIN_QUERY_LEN } from './consts'
import SearchBoxContainerDesktop from './SearchBoxContainerDesktop'
import SearchBoxContainerMobile from './SearchBoxContainerMobile'

import 'views/components/organisms/SearchBox/SearchBox.scss'

class SearchBoxContainer extends Component {
  static DEBOUNCE_AD_SLOT_BANNER_MS = 1000

  storage = new Storage('sessionStorage')

  controller = {}

  keyDownHandlers = {
    ArrowDown() {
      const {
        querySuggestions,
        products,
        focusedSuggestionIndex,
        focusedProductIndex,
      } = this.state
      const totalItems = querySuggestions?.length + products?.length

      if (totalItems === 0 || !this.state.shouldDisplayResults) {
        return
      }

      let newFocusedSuggestionIndex = focusedSuggestionIndex
      let newFocusedProductIndex = focusedProductIndex
      let displayedValue

      // If nothing is focused, focus on first suggestion
      if (
        focusedSuggestionIndex < querySuggestions.length - 1 &&
        focusedProductIndex < 0
      ) {
        newFocusedSuggestionIndex++
        newFocusedProductIndex = -1
        displayedValue = querySuggestions[newFocusedSuggestionIndex]
      } else if (
        // if last suggestion is focused
        focusedSuggestionIndex ===
        querySuggestions.length - 1
      ) {
        // Move from last suggestion to first product
        newFocusedSuggestionIndex = -1
        newFocusedProductIndex = 0
        displayedValue = products[newFocusedProductIndex].name
      } else if (
        focusedProductIndex >= 0 &&
        focusedProductIndex < products.length - 1
      ) {
        // Move through products
        newFocusedProductIndex++
        displayedValue = products[newFocusedProductIndex].name
      } else {
        // Cycle back to initial query
        newFocusedSuggestionIndex = -1
        newFocusedProductIndex = -1
        displayedValue = this.state.query
      }

      this.setState({
        displayedValue,
        focusedSuggestionIndex: newFocusedSuggestionIndex,
        focusedProductIndex: newFocusedProductIndex,
      })
    },

    ArrowUp() {
      const {
        querySuggestions,
        products,
        focusedSuggestionIndex,
        focusedProductIndex,
      } = this.state
      const totalItems = querySuggestions?.length + products?.length

      if (totalItems === 0 || !this.state.shouldDisplayResults) {
        return
      }

      let newFocusedSuggestionIndex = focusedSuggestionIndex
      let newFocusedProductIndex = focusedProductIndex
      let displayedValue

      if (focusedProductIndex > 0) {
        // Move up through products
        newFocusedProductIndex--
        displayedValue = products[newFocusedProductIndex].name
      } else if (focusedProductIndex === 0) {
        // Move from first product to last suggestion
        newFocusedProductIndex = -1
        newFocusedSuggestionIndex = querySuggestions.length - 1
        displayedValue = querySuggestions[newFocusedSuggestionIndex]
      } else if (focusedSuggestionIndex > 0) {
        // Move up through suggestions
        newFocusedSuggestionIndex--
        displayedValue = querySuggestions[newFocusedSuggestionIndex]
      } else if (focusedSuggestionIndex === 0) {
        // Cycle to original query
        newFocusedSuggestionIndex = -1
        newFocusedProductIndex = -1
        displayedValue = this.state.query
      } else {
        // If nothing is focused, focus on last product
        newFocusedProductIndex = products.length - 1
        displayedValue = products[newFocusedProductIndex].name
      }

      this.setState({
        displayedValue,
        focusedSuggestionIndex: newFocusedSuggestionIndex,
        focusedProductIndex: newFocusedProductIndex,
      })
    },
    Enter(event) {
      if (event.keyCode !== 13 || !this.state.shouldDisplayResults) {
        return
      }

      const {
        products,
        querySuggestions,
        displayedValue,
        focusedProductIndex,
        focusedSuggestionIndex,
        query,
      } = this.state

      if (focusedSuggestionIndex >= 0) {
        this.forwardToSearchResultsPage(
          querySuggestions[focusedSuggestionIndex]
        )
      } else {
        const selectedProduct = products[focusedProductIndex] || {}
        const { deeplink } = selectedProduct
        const deeplinkOfFirstSuggestedProduct = products[0]?.deeplink

        if (deeplink) {
          this.forwardToPDP(deeplink)
        } else if (products.length === 1 && deeplinkOfFirstSuggestedProduct) {
          this.forwardToPDP(deeplinkOfFirstSuggestedProduct)
        } else {
          this.forwardToSearchResultsPage(
            displayedValue || query,
            'search-submit'
          )
        }
      }
    },
    /**
     * Close the dropdown list with the search results
     */
    Escape() {
      this.hideResults()
    },
  }

  constructor(props) {
    super(props)

    this.state = this.getInitialState(props.searchedQuery)
    this.ComponentToRender = props.mobile
      ? SearchBoxContainerMobile
      : SearchBoxContainerDesktop
  }

  getInitialState = (query = '') => {
    const {
      publicConfig: { mountPoints, tenant, language },
      pageProperties: { coreShopEnvironment },
    } = this.props.publicRuntimeConfig

    const mountPointsInstance = new MountPointsClass({
      mountPoints,
      tenant,
      language,
      coreShopEnvironment,
    })

    let searchHistory = null
    if (SearchHistoryService.isEnabled) {
      searchHistory = SearchHistoryService.get()
    }

    return {
      query,
      products: [],
      redirectData: [],
      shouldDisplayResults: false,
      focusedProductIndex: -1,
      focusedSuggestionIndex: -1,
      displayedValue: query,
      mountPoints: mountPointsInstance,
      formAction: mountPointsInstance.getSearch({
        query: null,
        channel: SEARCH_CHANNEL_ALGOLIA,
      }),
      searchHistory,
      algoliaRequestSent: false,
    }
  }

  async componentDidMount() {
    this.fetchIfNeeded()
  }

  saveSuggestionToStorage = () => {
    this.addCurrentQueryToHistory()
    DataLayerSearchHistoryService.set({
      qSuggest: this.state.products.length,
      searchPhrase: this.state.query,
      searchType: 'Suggest',
    })
  }

  forwardToSearchResultsPage(query, eventName = '') {
    if (query) {
      const { mountPoints } = this.state

      window.location.href = mountPoints.getSearch({
        query,
        channel: SEARCH_CHANNEL_ALGOLIA,
        userToken: algoliaUserService.getToken(),
        trackingData: {
          eventName,
        },
      })
    }
  }

  forwardToPDP(deeplink) {
    if (deeplink) {
      window.location.href = deeplink.replace(/^\/+|^/, '/')
    }
  }

  resetState() {
    this.setState(this.getInitialState())
  }

  setSearchAbTestInfoToStorage = () => {
    const { abTestID, abTestVariantID } = this.state
    if (abTestID && abTestVariantID) {
      algoliaUserService.setSearchAbTestInfo({
        abTestID,
        abTestVariantID,
      })
    }
  }

  async fetchAndSetStatus() {
    await this.fetch()
    this.setSearchAbTestInfoToStorage()
    this.setState({
      algoliaRequestSent: true,
    })
  }

  fetchDebounced = debounce(async () => await this.fetchAndSetStatus(), 500)

  async fetchIfNeeded() {
    if (IS_JEST && this.state.query.length >= TYPEAHEAD_MIN_QUERY_LEN) {
      await this.fetchAndSetStatus()
    } else if (!this.state.algoliaRequestSent) {
      if (this.state.query.length === TYPEAHEAD_MIN_QUERY_LEN) {
        await this.fetchAndSetStatus()
      } else if (this.state.query.length > TYPEAHEAD_MIN_QUERY_LEN) {
        this.fetchDebounced()
      }
    }
  }

  cancelController() {
    if (typeof this.controller.cancel === 'function') {
      this.controller.cancel('Search query updated')
      this.controller = {}
    }

    return this
  }

  handleClear = () => {
    this.cancelController().resetState()
  }

  getSearchResultsAndSuggestions = () => {
    const { query } = this.state
    const { clientAPIService } = buildClientAPI()
    const userToken = algoliaUserService.getToken()
    const {
      publicConfig: { deviceClass },
      pageProperties: { expa, ruleContexts },
    } = this.props.publicRuntimeConfig

    if (!query.trim()) {
      return null
    }

    return clientAPIService.searchAlgolia(query, {
      userToken,
      deviceClass,
      expa,
      ruleContexts,
    })
  }

  async fetch() {
    this.cancelController()
    const {
      publicConfig: { cdnUrl, tenant },
      pageProperties: { experiments },
    } = this.props.publicRuntimeConfig

    const isVariantAttributesExperimentEnabled =
      isVariantAttributes(experiments)

    const isRebrandingEnabled = tenant === TENANT_IT
    const cdnEnabled = getExperiment(NEW_CDN, experiments)

    try {
      const { results: allResults } =
        await this.getSearchResultsAndSuggestions()
      const [searchResults, suggestions] = allResults
      const afterQuerySuggestions = processAlgoliaQuerySuggestions({
        ...suggestions,
      })
      const suggestQueries = afterQuerySuggestions || []

      const results = mapAlgoliaToSuggestResults({
        ...searchResults,
        cdnUrl,
        isVariantAttributesExperimentEnabled,
        isRebrandingEnabled,
        placeholderFromCdn: cdnEnabled.isEnabled,
      })

      if (results) {
        this.populateResults(results)
      }
      if (suggestQueries.length > 0) {
        this.setState({ querySuggestions: suggestQueries })
      }
    } catch (err) {
      logger.error(err)
    }
  }

  updateDisplayedValue = value => {
    this.setState({
      displayedValue: value,
    })
  }

  /**
   * The only place where query updates.
   * Doesn't let spaces to trigger fetch, but allows
   * trailing spaces for non-empty strings.
   */
  handleQueryUpdate = query => {
    const normalizedQuery = query?.trim()
      ? query.slice(0, ALGOLIA_QUERY_MAX_LENGTH)
      : ''

    const shouldDisplayResults =
      normalizedQuery.length >= TYPEAHEAD_MIN_QUERY_LEN

    store.dispatch({ type: QUERY_UPDATED, updatedQuery: normalizedQuery })
    this.debounceAdSlotBanner?.(normalizedQuery)
    /**
     * CRO-4329: reduce the number of algolia requests
     *
     * If the user only starts typing in the search field, we do not create the delay
     * and show him the results, not to damage the user experience. When he continues
     * typing, we make a 1 sec. delay in sending the algolia requests
     */

    this.setState(
      {
        query: normalizedQuery,
        displayedValue: normalizedQuery,
        shouldDisplayResults,
        algoliaRequestSent: false,
      },
      this.fetchIfNeeded
    )
  }

  handleInputUpdate = ({ target: { value = '' } }) => {
    this.handleQueryUpdate(value)
    handleDummySearchBoxUpdate(value)
  }

  handleKeyUp = event => {
    if (this.keyDownHandlers[event.key]) {
      event.preventDefault()
      this.keyDownHandlers[event.key].call(this, event)
    }
  }

  addCurrentQueryToHistory = () => {
    const { query } = this.state
    if (SearchHistoryService.isEnabled && query?.length > 0) {
      const searchHistory = SearchHistoryService.add(query)
      this.setState({ searchHistory })
    }
  }

  handleClearHistory = () => {
    if (SearchHistoryService.isEnabled) {
      const searchHistory = SearchHistoryService.clear()
      this.setState({
        searchHistory,
      })
    }
  }

  handleSubmit = event => {
    event.preventDefault()

    this.addCurrentQueryToHistory()

    const { query, products, focusedProductIndex } = this.state
    // If it is not Enter pressed on a selected product
    if (focusedProductIndex < 0 && query?.length > 0) {
      const deeplink = products[0]?.deeplink
      if (products?.length === 1 && deeplink) {
        this.forwardToPDP(deeplink)
        return
      }

      // Why change location manually:
      // <input name="query" /> inside <form method="get" action="?myparam=1" />
      // overrides form's `action` params (drops all, sets only `query`)
      this.forwardToSearchResultsPage(query, 'search-submit')
    }
  }

  handleShowAll = event => {
    event.preventDefault()
    this.addCurrentQueryToHistory()

    const { query } = this.state
    this.forwardToSearchResultsPage(query)
  }

  populateResults = ({
    suggestions,
    redirectData,
    abTestID,
    abTestVariantID,
    withHighlightedProduct,
  }) => {
    const products = suggestions.filter(isValidProduct)
    const shouldDisplayResults = Boolean(products.length)

    this.setState({
      products,
      redirectData,
      shouldDisplayResults,
      abTestID,
      abTestVariantID,
      focusedProductIndex: -1,
      withHighlightedProduct,
    })
  }

  hideResults = () => {
    this.setState({
      shouldDisplayResults: false,
      focusedProductIndex: -1,
      focusedSuggestionIndex: -1,
      querySuggestions: [],
    })
  }

  render() {
    const { mobile, inputId, publicRuntimeConfig, pageType } = this.props
    const {
      products,
      redirectData,
      shouldDisplayResults,
      query,
      focusedProductIndex,
      focusedSuggestionIndex,
      displayedValue,
      formAction,
      searchHistory,
      withHighlightedProduct,
      querySuggestions,
    } = this.state
    const { ComponentToRender } = this

    const childProps = {
      mobile,
      query,
      inputId,
      products,
      redirectData,
      displayResults: shouldDisplayResults,
      focusedProductIndex,
      focusedSuggestionIndex,
      displayedValue,
      formAction,
      publicRuntimeConfig,
      pageType,
      handleShowAll: this.handleShowAll,
      hideResults: this.hideResults,
      handleQueryUpdate: this.handleQueryUpdate,
      handleInputUpdate: this.handleInputUpdate,
      handleClear: this.handleClear,
      handleKeyUp: this.handleKeyUp,
      handleSubmit: this.handleSubmit,
      handleItemClick: this.saveSuggestionToStorage,
      handleValueChange: this.updateDisplayedValue,
      handleClearHistory: this.handleClearHistory,
      searchHistory,
      withHighlightedProduct,
      querySuggestions,
    }

    return <ComponentToRender {...childProps} />
  }
}

SearchBoxContainer.propTypes = {
  inputId: string.isRequired,
  mobile: bool,
  searchedQuery: string,
  pageType: oneOf(PAGE_TYPES),
  publicRuntimeConfig: shape({
    pageProperties: shape({
      coreShopEnvironment: string,
      expa: string,
      ruleContexts: arrayOf(string),
    }),
    publicConfig: shape({
      mountPoints: objectOf(string),
      tenant: string,
      language: string,
      cdnUrl: string,
    }),
  }).isRequired,
}

SearchBoxContainer.defaultProps = {
  mobile: false,
  searchedQuery: '',
  pageType: null,
}

export { SearchBoxContainer as UnwrappedSearchBoxcontainer }
export default withShopAndRuntimeConfig(SearchBoxContainer)
